import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import {
  useGetPageByIdQuery,
  useUpdatePageMutation,
  useCreatePageMutation,
  useGetTemplatesQuery,
  LandingPage,
  LandingPageConfig,
  CreateLandingPageRequest,
  UpdateLandingPageRequest,
} from "../../api/pagesApi";
import { useGetCustomersQuery } from "../../api/customerApi";
import type { Customer } from "../../types/customer";
import { useAppSelector } from "../../hooks/redux";

const Container = styled.div`
  padding: 24px;
  max-width: 1000px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  margin: 0;
  color: #1f2937;
  font-size: 28px;
  font-weight: 700;
`;

const BackButton = styled.button`
  background: #f9fafb;
  color: #374151;
  border: 1px solid #d1d5db;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const SectionTitle = styled.h3`
  margin: 0 0 16px 0;
  color: #1f2937;
  font-size: 18px;
  font-weight: 600;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #374151;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background: #f9fafb;
    color: #6b7280;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background: #f9fafb;
    color: #6b7280;
  }
`;

const Textarea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ColorInput = styled.input`
  padding: 4px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  width: 80px;
  height: 40px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ColorGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
`;

const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: 600;
  color: #374151;
  font-size: 14px;
`;

const ToggleInput = styled.input`
  width: 16px;
  height: 16px;
`;

const StatusBadge = styled.span<{ isActive: boolean }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${(props) => (props.isActive ? "#10b981" : "#ef4444")};
  color: white;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
`;

const Button = styled.button<{ variant?: "primary" | "secondary" | "danger" }>`
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${(props) => {
    switch (props.variant) {
      case "primary":
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
          }
        `;
      case "danger":
        return `
          background: #ef4444;
          color: white;
          &:hover {
            background: #dc2626;
          }
        `;
      default:
        return `
          background: white;
          color: #374151;
          border: 1px solid #d1d5db;
          &:hover {
            background: #f9fafb;
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #6b7280;
`;

const ErrorState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #ef4444;
  text-align: center;
`;

const HelpText = styled.p`
  margin: 0;
  font-size: 12px;
  color: #6b7280;
`;

interface PageDetailProps {
  pageId?: string;
  onBack: () => void;
  onSave?: (page: LandingPage) => void;
}

export const PageDetail: React.FC<PageDetailProps> = ({
  pageId,
  onBack,
  onSave,
}) => {
  const isCreateMode = !pageId;
  const { user } = useAppSelector((state) => state.auth);
  const isAdmin = user?.role === "admin";

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    template: "modern",
    customerId: user?.customerId || "",
    domain: "",
    isActive: true,
    config: {
      heroTitle: "",
      heroSubtitle: "",
      ctaText: "Get Started",
      theme: "default",
      colors: {
        primary: "#3B82F6",
        secondary: "#1F2937",
        accent: "#10B981",
      },
    } as LandingPageConfig,
  });

  const [isSaving, setIsSaving] = useState(false);

  // API queries
  const {
    data: pageData,
    isLoading: pageLoading,
    error: pageError,
  } = useGetPageByIdQuery(
    { id: pageId!, includeConfig: true },
    { skip: isCreateMode }
  );

  const { data: templates } = useGetTemplatesQuery();

  const { data: customersData } = useGetCustomersQuery(
    { page: 1, limit: 100 },
    { skip: !isAdmin }
  );

  const [updatePage] = useUpdatePageMutation();
  const [createPage] = useCreatePageMutation();

  // Load page data on mount
  useEffect(() => {
    if (pageData && !isCreateMode) {
      setFormData({
        title: pageData.title,
        slug: pageData.slug,
        template: pageData.template,
        customerId: pageData.customer.id,
        domain: pageData.domain || "",
        isActive: pageData.isActive,
        config: {
          ...pageData.config,
          colors: {
            primary: pageData.config?.colors?.primary || "#3B82F6",
            secondary: pageData.config?.colors?.secondary || "#1F2937",
            accent: pageData.config?.colors?.accent || "#10B981",
          },
        },
      });
    }
  }, [pageData, isCreateMode]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleConfigChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        [field]: value,
      },
    }));
  };

  const handleColorChange = (colorType: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        colors: {
          ...prev.config.colors,
          [colorType]: value,
        },
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      if (isCreateMode) {
        const createData: CreateLandingPageRequest = {
          title: formData.title,
          slug: formData.slug,
          template: formData.template,
          customerId: formData.customerId,
          domain: formData.domain || undefined,
          config: formData.config,
        };
        const result = await createPage(createData).unwrap();
        onSave?.(result);
      } else {
        const updateData: UpdateLandingPageRequest = {
          title: formData.title,
          isActive: formData.isActive,
          config: formData.config,
        };
        const result = await updatePage({
          id: pageId!,
          data: updateData,
        }).unwrap();
        onSave?.(result);
      }
    } catch (error) {
      console.error("Failed to save page:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isCreateMode && pageLoading) {
    return (
      <Container>
        <LoadingState>Loading page details...</LoadingState>
      </Container>
    );
  }

  if (!isCreateMode && pageError) {
    return (
      <Container>
        <ErrorState>Failed to load page details</ErrorState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          {isCreateMode ? "Create Landing Page" : `Edit ${formData.title}`}
          {!isCreateMode && (
            <StatusBadge
              isActive={formData.isActive}
              style={{ marginLeft: 16 }}
            >
              {formData.isActive ? "Active" : "Inactive"}
            </StatusBadge>
          )}
        </Title>
        <BackButton onClick={onBack}>Back to Pages</BackButton>
      </Header>

      <Form onSubmit={handleSubmit}>
        <Section>
          <SectionTitle>Basic Information</SectionTitle>
          <FormGroup>
            <Label>Page Title</Label>
            <Input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
              placeholder="Enter page title"
              required
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Label>URL Slug</Label>
              <Input
                type="text"
                value={formData.slug}
                onChange={(e) => handleInputChange("slug", e.target.value)}
                placeholder="url-friendly-slug"
                required
                disabled={!isCreateMode}
              />
              <HelpText>URL slug cannot be changed after creation</HelpText>
            </FormGroup>

            <FormGroup>
              <Label>Template</Label>
              <Select
                value={formData.template}
                onChange={(e) => handleInputChange("template", e.target.value)}
                disabled={!isCreateMode}
              >
                {templates &&
                  Object.entries(templates?.data).map(([key, template]) => (
                    <option key={key} value={key}>
                      {template.name} - {template.description}
                    </option>
                  ))}
              </Select>
              <HelpText>Template cannot be changed after creation</HelpText>
            </FormGroup>
          </FormRow>

          {isAdmin && (
            <FormRow>
              <FormGroup>
                <Label>Customer</Label>
                <Select
                  value={formData.customerId}
                  onChange={(e) =>
                    handleInputChange("customerId", e.target.value)
                  }
                  required
                  disabled={!isCreateMode}
                >
                  <option value="">Select a customer</option>
                  {customersData?.data.customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} ({customer.email})
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Custom Domain (Optional)</Label>
                <Input
                  type="text"
                  value={formData.domain}
                  onChange={(e) => handleInputChange("domain", e.target.value)}
                  placeholder="custom-domain.com"
                  disabled={!isCreateMode}
                />
              </FormGroup>
            </FormRow>
          )}

          {!isCreateMode && (
            <FormGroup>
              <Toggle>
                <ToggleInput
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    handleInputChange("isActive", e.target.checked)
                  }
                />
                Page is active and visible to visitors
              </Toggle>
            </FormGroup>
          )}
        </Section>

        <Section>
          <SectionTitle>Content Configuration</SectionTitle>
          <FormGroup>
            <Label>Hero Title</Label>
            <Input
              type="text"
              value={formData.config.heroTitle || ""}
              onChange={(e) => handleConfigChange("heroTitle", e.target.value)}
              placeholder="Main headline for your page"
            />
          </FormGroup>

          <FormGroup>
            <Label>Hero Subtitle</Label>
            <Textarea
              value={formData.config.heroSubtitle || ""}
              onChange={(e) =>
                handleConfigChange("heroSubtitle", e.target.value)
              }
              placeholder="Supporting text that explains your offer"
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Label>Call-to-Action Text</Label>
              <Input
                type="text"
                value={formData.config.ctaText || ""}
                onChange={(e) => handleConfigChange("ctaText", e.target.value)}
                placeholder="Get Started"
              />
            </FormGroup>

            <FormGroup>
              <Label>Theme</Label>
              <Select
                value={formData.config.theme || "default"}
                onChange={(e) => handleConfigChange("theme", e.target.value)}
              >
                <option value="default">Default</option>
                <option value="dark">Dark</option>
                <option value="minimal">Minimal</option>
              </Select>
            </FormGroup>
          </FormRow>
        </Section>

        <Section>
          <SectionTitle>Color Scheme</SectionTitle>
          <ColorGroup>
            <FormGroup>
              <Label>Primary Color</Label>
              <ColorInput
                type="color"
                value={formData.config.colors?.primary || "#3B82F6"}
                onChange={(e) => handleColorChange("primary", e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>Secondary Color</Label>
              <ColorInput
                type="color"
                value={formData.config.colors?.secondary || "#1F2937"}
                onChange={(e) => handleColorChange("secondary", e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>Accent Color</Label>
              <ColorInput
                type="color"
                value={formData.config.colors?.accent || "#10B981"}
                onChange={(e) => handleColorChange("accent", e.target.value)}
              />
            </FormGroup>
          </ColorGroup>
        </Section>

        <Actions>
          <Button type="button" onClick={onBack}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSaving}>
            {isSaving
              ? "Saving..."
              : isCreateMode
              ? "Create Page"
              : "Save Changes"}
          </Button>
        </Actions>
      </Form>
    </Container>
  );
};
